import { xray } from 'lib/xray';
import React, { type MouseEvent, type ReactElement, type RefObject, memo, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { hideCleanerInMenu } from 'reactApp/appHelpers/featuresHelpers/features/hideCleanerInMenu';
import { REFERRAL_ANALYTIC_NAME, referralProgramSpacePromoEnabled } from 'reactApp/appHelpers/featuresHelpers/features/referralProgram';
import { useSendSpaceMenuAnalytics } from 'reactApp/hooks/UseSendSpaceMenuAnalytics';
import { getHideCleanerInSpaceFeature } from 'reactApp/modules/features/features.selectors';
import { DropdownItemAction } from 'reactApp/ui/DropdownItemAction/DropdownItemAction';
import { type ListItem, DropdownFont, DropdownList, DropdownTheme } from 'reactApp/ui/DropdownList/DropdownList';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import opener from 'reactApp/utils/opener';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './SingleSpaceMenu.css';
import { type IMenuItem, EMenuItemId, getSpaceMenuList } from './SingleSpaceMenu.data';

interface IProps {
    isSubscriptionsPage?: boolean;
    isMobile: boolean;
    isMySubscription?: boolean;
    onClose: (event?: MouseEvent) => void;
    parentRef?: RefObject<HTMLElement>;
}

export const SingleSpaceMenu = memo(
    ({ isSubscriptionsPage = false, isMobile, onClose, parentRef, isMySubscription = false }: IProps): ReactElement | null => {
        const sendAnalytics = useSendSpaceMenuAnalytics(isMobile);

        const hideCleaner = useSelector(getHideCleanerInSpaceFeature);

        const title = 'Единое хранилище';

        const onItemClick = useCallback(
            (item: IMenuItem) => () => {
                if (item.id === EMenuItemId.cleaner) {
                    xray.send(`quota_cleaner_space_open${isMobile ? '_mob' : ''}`);
                }
                if (item.id === EMenuItemId.referralProgram) {
                    emitAnalyticEvent(AnalyticEventNames.REFERRAL_BONUS_SPACE_CLICK);
                    sendPaymentGa({
                        action: 'click',
                        eventCategory: ECategoryGa.referalFriendsBonus,
                        name: REFERRAL_ANALYTIC_NAME,
                    });
                }
                sendAnalytics(item.id);
                opener(item.url);
            },
            [sendAnalytics]
        );

        const list = useMemo(
            () =>
                getSpaceMenuList({
                    isSubscriptionsPage,
                    // tempexp_18086-next-line
                    hideReferral: !referralProgramSpacePromoEnabled,
                    hideCleaner: (isMobile && hideCleaner) || hideCleanerInMenu,
                    isMySubscription,
                }).map((item) => ({ ...item, onClick: onItemClick(item) })),
            [hideCleaner, isMobile, isMySubscription, isSubscriptionsPage, onItemClick]
        );

        const findDropdownPosition = (): { posX: number; posY: number } => {
            let posX = 0;
            let posY = 0;

            if (parentRef?.current) {
                const elRect = parentRef.current.getBoundingClientRect();

                posY = elRect.height + window.scrollY + elRect.top + 2;
                posX = elRect.left + window.scrollX;
            }

            return { posX, posY };
        };

        const renderDropdownItem = (item: ListItem): ReactElement => {
            return (
                <DropdownItemAction
                    classNameText={styles.itemText}
                    text={item.text}
                    icon={item.icon}
                    mod={DropdownTheme.space}
                    isNew={item.isNew}
                />
            );
        };

        useEffect(() => {
            sendAnalytics('menu');
        }, []);

        if (!list.length) {
            return null;
        }

        if (isMobile) {
            const mobileTitle = <div className={styles.title}>{title}</div>;

            return (
                <MobileDialog title={mobileTitle} id="mobile-space-menu" onClose={onClose} closeOnDimmerClick mod="base" topmost>
                    <div className={styles.menu}>
                        {list.map((item) => (
                            <div className={styles.menuItem} key={item.id} onClick={item.onClick} data-name={item.id}>
                                <div className={styles.icon} data-name="icon">
                                    {item.iconMobile}
                                </div>
                                <div className={styles.text}>{item.text}</div>
                            </div>
                        ))}
                    </div>
                </MobileDialog>
            );
        }

        return (
            <DropdownList
                gaId="datalist-control"
                list={list}
                close={onClose}
                closeOnScroll
                closeOnMouseLeave
                parentRef={parentRef}
                calcPosition={findDropdownPosition}
                renderItem={renderDropdownItem}
                font={DropdownFont.VKSansDisplay}
                theme={DropdownTheme.space}
                itemClassName={styles.item}
            />
        );
    }
);

SingleSpaceMenu.displayName = 'SingleSpaceMenu';
